import { type Ref, ref } from 'vue';
import {
  addMinutes,
  differenceInMinutes,
  startOfDay,
  addDays,
  max as maxDate,
  min as minDate,
  subMinutes
} from 'date-fns';
import type { Calendar } from './useCalendar'
import type { SlotDate } from './Cell'
import { isBetween, isBetweenStrictly } from '../comparisons';

export interface ISelectOptions {
  allowBetweenDays?: boolean
  minLength?: Ref<number> //minutes
  fixLength?: Ref<number> //minutes
}

export function isSameDay(from: Date, to: Date) {
  /** 00:00 относится к прошлому дню */
  const almostTo = subMinutes(to, 1);
  return from.getFullYear() === almostTo.getFullYear()
    && from.getMonth() === almostTo.getMonth()
    && from.getDate() === almostTo.getDate();
}

export function useSlotSelector(cueCalendar: Calendar, selected: Ref<SlotDate | undefined>, {
  minLength = ref(0),
  fixLength = ref(0),
  allowBetweenDays = false,
}: ISelectOptions) {
  function isBetweenDaysError({ from, to }: SlotDate) {
    const isBetween = !isSameDay(from, to);
    return isBetween && !allowBetweenDays;
  }

  function set(newSelect?: SlotDate, disableMinTimeLength?: boolean) {
    if (!newSelect) return (selected.value = undefined);

    const from = new Date(newSelect.from);
    let to = new Date(newSelect.to);

    if (fixLength.value) {
      to = addMinutes(from, fixLength.value);
    } else if (!disableMinTimeLength && minLength.value) {
      const dur = differenceInMinutes(to, from);
      if (dur < minLength.value) {
        to = addMinutes(to, minLength.value - dur);
        if (isBetweenDaysError({ from, to })) {
          to = startOfDay(addDays(from, 1));
        }
      }
    }

    const subset = cueCalendar.getAvailableSubset({ from, to });
    if (cueCalendar.isCellDisabled(subset)) return;
    return (selected.value = subset);
  }

  function push({ from, to }: Partial<SlotDate>) {
    if (!selected.value || !from || !to) return;

    const newSelect = { ...selected.value };
    if (+from < +newSelect.from) newSelect.from = from;
    if (+to > +newSelect.to) newSelect.to = to;

    const updated = set(newSelect, true)
    if (!updated) set({ from, to }, true)
  }

  function shift({ from, to }: Partial<SlotDate>) {
    if (!selected.value) return
    const newSelect = { ...selected.value }

    if (from && +from > +newSelect.from) newSelect.from = from
    if (to && +to < +newSelect.to) newSelect.to = to

    set(newSelect, true)
  }

  function check({ from, to }: SlotDate): boolean {
    if (!selected.value) return false
    return +selected.value.from <= +from && +to <= +selected.value.to
  }

  function clear() {
    set()
  }

  interface SelectOptions {
    overload?: boolean
    /**
     * single: используем обычную логику добавления слотов
     * range: всегда стараемся ДОБАВИТЬ новые слоты к выбранным
     */
    mode?: 'single' | 'range'
  }

  function select({ from, to }: SlotDate, options?: SelectOptions) {
    const { overload = false, mode = 'single' } = options || {}
    const s = selected.value;

    if (!s) return set({ from, to });

    const sameFrom = +from === +s.from;
    const sameTo = +to === +s.to;
    const double = sameFrom && sameTo;

    if (double) return clear(); // одинаковый выбор — сброс

    if (overload) return set({ from, to }); // overload - всегда новое значение

    const fromIn = isBetween(s.from, from, s.to);
    const fromInset = isBetweenStrictly(s.from, from, s.to);

    //Если есть fixLength, то надо сделать set на текущих данных
    if (fixLength.value) {
      if (fromIn && !fromInset) return clear(); // граничный повтор — сброс
      return set({ from, to });
    }

    const toIn = isBetween(s.from, to, s.to);
    const toInset = isBetweenStrictly(s.from, to, s.to);

    //Если выбран слот между краями селекта, то ставим этот слот
    const isInset = !sameFrom && !sameTo && fromInset && toInset;
    if (isInset) return set({ from, to });

    const isAnotherTime = !(sameFrom || sameTo || fromIn || toIn);

    const betweenError = isBetweenDaysError({ from: minDate([s.from, from]), to: maxDate([s.to, to]) });

    if (isAnotherTime) {
      if (mode === 'range' && !betweenError) return push({ from, to });
      return set({ from, to });
    }

    //Если один слот продолжение другого, то добавляем
    if (+to === +s.from || +from === +s.to) return betweenError ? set({ from, to }) : push({ from, to });

    //Если выбран крайний слот - обрезаем
    if (sameFrom && toIn) return shift({ from: to });
    if (sameTo && fromIn) return shift({ to: from });
  }

  function getIsSelected(date: SlotDate) {
    return !!selected.value && (+selected.value.from <= +date.from) && (+date.to <= +selected.value.to)
  }

  return {
    clear,
    check,
    select,
    selected,
    getIsSelected,
  };
}

export type SlotSelect = ReturnType<typeof useSlotSelector>;
